import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';
import { getIntlLocale } from '../utils/dateLocale';

const TIME_ZONE = 'Europe/Istanbul';
const DAY_INDEX_KEY = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const capitalizeLocalized = (value, locale) => {
  if (!value) return value;
  const firstChar = value.charAt(0).toLocaleUpperCase(locale);
  const rest = value.slice(1).toLocaleLowerCase(locale);
  return `${firstChar}${rest}`;
};

const createFormatters = (language) => {
  const locale = getIntlLocale(language);
  return {
    locale,
    dateFormatter: new Intl.DateTimeFormat(locale, {
      timeZone: TIME_ZONE,
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }),
    weekdayFormatter: new Intl.DateTimeFormat(locale, {
      timeZone: TIME_ZONE,
      weekday: 'long',
    }),
    timeFormatter: new Intl.DateTimeFormat(locale, {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
  };
};

const weekdayFormatterEN = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  weekday: 'long',
});

const getZonedDate = (date) => new Date(date.toLocaleString('en-US', { timeZone: TIME_ZONE }));

const getTimeHHmm = (date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const getDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initialFormatters = createFormatters(i18n.language);
const initialZonedDate = getZonedDate(new Date());
const initialWeekdayRaw = weekdayFormatterEN.format(initialZonedDate).toLowerCase();
const initialFormattedWeekday = capitalizeLocalized(
  initialFormatters.weekdayFormatter.format(initialZonedDate),
  initialFormatters.locale
);

const TimeContext = createContext({
  timeZone: TIME_ZONE,
  now: initialZonedDate,
  formattedDate: initialFormatters.dateFormatter.format(initialZonedDate),
  formattedTime: initialFormatters.timeFormatter.format(initialZonedDate),
  formattedWeekday: initialFormattedWeekday,
  timeHHmm: getTimeHHmm(initialZonedDate),
  dayKey: DAY_INDEX_KEY[initialZonedDate.getDay()] ?? initialWeekdayRaw,
  dateKey: getDateKey(initialZonedDate),
});

export const TimeProvider = ({ children }) => {
  const [now, setNow] = useState(() => new Date());
  const [language, setLanguage] = useState(() => i18n.language);
  const [currentMinute, setCurrentMinute] = useState(() => {
    const initialDate = getZonedDate(new Date());
    return getTimeHHmm(initialDate);
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newDate = new Date();
      setNow(newDate);

      const zonedDate = getZonedDate(newDate);
      const newMinute = getTimeHHmm(zonedDate);
      setCurrentMinute((prev) => {
        if (prev !== newMinute) {
          return newMinute;
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setLanguage(lng);
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, []);

  const value = useMemo(() => {
    const formatters = createFormatters(language);
    const zonedDate = getZonedDate(now);
    const formattedDate = formatters.dateFormatter.format(zonedDate);
    const formattedWeekday = capitalizeLocalized(
      formatters.weekdayFormatter.format(zonedDate),
      formatters.locale
    );
    const formattedTime = formatters.timeFormatter.format(zonedDate);
    const weekDayKeyRaw = weekdayFormatterEN.format(zonedDate).toLowerCase();
    const dateKey = getDateKey(zonedDate);

    return {
      timeZone: TIME_ZONE,
      now: zonedDate,
      formattedDate,
      formattedTime,
      formattedWeekday,
      timeHHmm: currentMinute,
      dayKey: DAY_INDEX_KEY[zonedDate.getDay()] ?? weekDayKeyRaw,
      dateKey,
    };
  }, [now, currentMinute, language]);

  return <TimeContext.Provider value={value}>{children}</TimeContext.Provider>;
};

export const useTime = () => useContext(TimeContext);

export default TimeContext;
